class CartUpsell extends HTMLElement {
  constructor() {
    super();

    this.track = this.querySelector("[data-js-track]");
    this.prevButton = this.querySelector("[data-js-prev]");
    this.nextButton = this.querySelector("[data-js-next]");
    this.items = [...this.querySelectorAll("[data-js-cart-upsell-item]")];
    this.nav = this.querySelector("[data-js-nav]");
    this.updateNavigation = this.updateNavigation.bind(this);
    this.applyFilters = this.applyFilters.bind(this);
  }

  connectedCallback() {
    if (!this.track) return;

    if (this.prevButton) {
      this.prevButton.addEventListener("click", () => this.scrollByItem(-1));
    }
    if (this.nextButton) {
      this.nextButton.addEventListener("click", () => this.scrollByItem(1));
    }

    this.track.addEventListener("scroll", this.updateNavigation, {
      passive: true,
    });

    // The drawer is rendered as display:none and only shown when it opens, so at
    // upgrade time the track measures zero and would look like it is at both ends
    // at once. Watching the track re-runs the check the moment it gets a layout,
    // and covers viewport resizes too.
    if ("ResizeObserver" in window) {
      this.observer = new ResizeObserver(this.updateNavigation);
      this.observer.observe(this.track);
    } else {
      window.addEventListener("resize", this.updateNavigation);
    }

    // The cart total lives in #AjaxCartForm, which the theme replaces wholesale on
    // every add / remove / quantity change, so re-filter whenever it is rewritten.
    const cartForm = document.getElementById("AjaxCartForm");
    if (cartForm) {
      this.cartObserver = new MutationObserver(this.applyFilters);
      this.cartObserver.observe(cartForm, { childList: true, subtree: true });
    }

    this.applyFilters();
    this.updateNavigation();
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
    if (this.cartObserver) this.cartObserver.disconnect();
    window.removeEventListener("resize", this.updateNavigation);
  }

  /* How much is still missing for free shipping, in cents, or null when the shop
     has no threshold configured. Both numbers come from the theme's own
     <shipping-notice>, so the drawer and this block can never disagree. */
  get freeShippingGap() {
    const notice = document.querySelector(
      "shipping-notice[data-cart-total-amount]"
    );
    if (!notice) return null;

    const threshold = Number(notice.getAttribute("data-free-shipping"));
    const cartTotal = Number(notice.getAttribute("data-cart-total-amount"));
    if (!threshold || Number.isNaN(cartTotal)) return null;

    const rate = Number(window.Shopify?.currency?.rate) || 1;
    return Math.round(threshold * rate) - cartTotal;
  }

  /* Show only the add-ons that on their own would carry the cart over the free
     shipping threshold, cheapest first, so the smallest qualifying add-on leads.

     The shop's priciest item (~79) is well under the threshold (199), so that only
     works for the last stretch. Below it nothing can close the gap alone; there the
     full list is shown biggest-first, because leading with add-ons that barely move
     the total would be the least useful order.

     Once the threshold is covered the gap stops being a ranking, so the order goes
     random instead of always fronting the same cheapest few. */
  applyFilters() {
    if (!this.items.length) return;

    // Suggesting something the customer already put in the cart wastes a slot, so
    // this exclusion is absolute: unlike the price filter below it has no fallback.
    const inCart = this.cartProductIds;
    const candidates = this.items.filter(
      (item) => !inCart.has(item.dataset.productId)
    );

    this.items.forEach((item) => {
      if (!candidates.includes(item)) item.hidden = true;
    });

    // Everything on offer is already in the cart: there is nothing left to suggest.
    this.hidden = candidates.length === 0;
    if (this.hidden) return;

    const gap = this.freeShippingGap;
    const priceOf = (item) => Number(item.dataset.price);
    const belowThreshold = gap !== null && gap > 0;
    const canCloseGap =
      belowThreshold && candidates.some((item) => priceOf(item) >= gap);

    candidates.forEach((item) => {
      const price = priceOf(item);
      item.hidden = canCloseGap && (Number.isNaN(price) || price < gap);
    });

    // candidates keeps the cheapest-first order the snippet rendered.
    let order;
    if (!belowThreshold) {
      // Free shipping is already covered, so there is no smallest-add-on-that-
      // closes-the-gap to lead with. Cheapest-first would just keep promoting the
      // same two products, so spread the exposure across the whole list instead.
      const remaining = new Set(candidates);
      order = this.shuffledItems.filter((item) => remaining.has(item));
    } else if (canCloseGap) {
      order = candidates;
    } else {
      order = [...candidates].reverse();
    }
    order.forEach((item) => this.track.append(item));

    this.track.scrollLeft = 0;
    this.updateNavigation();
  }

  /* One random order, drawn once per page load and then reused. applyFilters runs
     on every cart change, so reshuffling each time would move the cards under the
     customer's finger whenever they adjust a quantity. Caching the order keeps it
     steady while products drop out of it as they are added to the cart. */
  get shuffledItems() {
    if (!this._shuffled) {
      const items = [...this.items];
      // Fisher-Yates; sort(() => Math.random() - 0.5) is not a uniform shuffle.
      for (let i = items.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [items[i], items[j]] = [items[j], items[i]];
      }
      this._shuffled = items;
    }

    return this._shuffled;
  }

  /* Product ids currently in the cart, read fresh every time: the theme replaces
     #AjaxCartForm wholesale on each add / remove / quantity change. */
  get cartProductIds() {
    const form = document.getElementById("AjaxCartForm");
    if (!form) return new Set();

    return new Set(
      [...form.querySelectorAll("[data-js-cart-item]")]
        .map((item) => item.dataset.productId)
        .filter(Boolean)
    );
  }

  get isRTL() {
    return document.documentElement.getAttribute("dir") === "rtl";
  }

  /* Width of one card plus the gap. It has to be measured on a card the price
     filter left visible: cards are rendered cheapest first, so the first one in the
     DOM is usually a hidden one, and a hidden card measures 0. */
  get step() {
    const item = this.items.find((i) => !i.hidden && i.offsetWidth > 0);
    if (!item) return this.track.clientWidth;

    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    return item.offsetWidth + gap;
  }

  scrollByItem(direction) {
    this.track.scrollBy({
      left: this.step * direction * (this.isRTL ? -1 : 1),
      behavior: "smooth",
    });
  }

  updateNavigation() {
    // No layout yet (the drawer is still closed): leave the buttons alone rather
    // than deriving a bogus state from zero-sized measurements.
    if (!this.track.clientWidth) return;

    const maxScroll = this.track.scrollWidth - this.track.clientWidth;
    const position = Math.abs(this.track.scrollLeft);

    // Arrows that can only nudge the row by a few pixels read as broken. With two
    // cards left after filtering there is barely 60px to scroll, so drop the
    // controls entirely unless a whole card can be advanced.
    if (this.nav) this.nav.hidden = maxScroll < this.step;

    if (this.prevButton) {
      this.prevButton.disabled = position <= 1;
    }
    if (this.nextButton) {
      this.nextButton.disabled = position >= maxScroll - 1;
    }
  }
}

customElements.define("cart-upsell", CartUpsell);
